/**
 * Per-row design entry point — an F-27 selection-toolbar action.
 *
 * The same shape as the translate button, deliberately: select cells in a
 * column that is not Original Script, press the icon, and every selected row
 * gets a storyboard frame generated from its own script line plus the board's
 * art direction. That is the "design for each part" pass; the theme pass lives
 * in the panel.
 *
 * Registration is a module side-effect, like `translate/action.tsx` — the
 * feature layer imports this file for its effect, and nothing has to know the
 * action exists.
 */
import { useState } from "react";

import { ApiError, startDesignRowImages } from "../../core/api.js";
import type { BoardSession } from "../../core/session.js";
import {
  registerToolbarAction,
  type ToolbarActionContext,
} from "../../registries/toolbar-actions/index.js";
import { showToast } from "../../ui/toast.js";
import { canDesignRows, isRowDesignVisible, type RowDesignFacts } from "./row-eligibility.js";

/**
 * Rows one run may cover. Mirrors the server's `DESIGN_MAX_ROW_IMAGES` default;
 * the server enforces its own configured value, so a mismatch costs a 400 with
 * a clear message rather than a wrong-looking UI.
 */
export const MAX_ROW_DESIGNS = 12;

function factsFor(session: BoardSession, sheetId: string): RowDesignFacts {
  return {
    sourceColId: session.board.sourceColumnId(sheetId),
    hasArtDirection: session.board.hasArtDirection(),
    maxRows: MAX_ROW_DESIGNS,
  };
}

function eligibilityFor(ctx: ToolbarActionContext) {
  const sel = ctx.selection;
  if (sel.kind !== "cells") return canDesignRows(sel, { sourceColId: undefined, hasArtDirection: false, maxRows: MAX_ROW_DESIGNS });
  return canDesignRows(sel, factsFor(ctx.session, sel.sheetId));
}

const iconDesignRows = (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
    <rect x="2" y="3" width="12" height="10" rx="1.5" />
    <path d="M2 10.5l3.5-3 2.5 2 2-1.5L14 11" />
    <circle cx="6" cy="6" r="1.2" />
  </svg>
);

/**
 * Confirm before spending. This is the one action in the app where a single
 * click can mean a dozen paid image calls, so it states the count plainly and
 * asks — the translate dialog sets the same precedent for a cheaper operation.
 */
function DesignRowsPopover({ ctx }: { ctx: ToolbarActionContext }) {
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const eligibility = eligibilityFor(ctx);

  if (!eligibility.ok) {
    return (
      <div className="design-rows-popover">
        <p className="design-hint">
          Write the video&apos;s design theme first — each row&apos;s frame is generated from it.
        </p>
      </div>
    );
  }

  const { rowIds, colId } = eligibility;
  const sel = ctx.selection;
  const sheetId = sel.kind === "cells" ? sel.sheetId : "";

  async function run() {
    setError(null);
    setStarting(true);
    try {
      const result = await startDesignRowImages(ctx.session.boardId, sheetId, colId, [...rowIds]);
      showToast(
        result.skipped > 0
          ? `Designing ${result.total} rows — ${result.skipped} had no script and were skipped.`
          : `Designing ${result.total} rows…`,
      );
      ctx.closePopover();
    } catch (err) {
      setError(describeRowError(err));
    } finally {
      setStarting(false);
    }
  }

  return (
    <div className="design-rows-popover">
      <p className="design-rows-count">
        Generate a design for <strong>{rowIds.length}</strong>{" "}
        {rowIds.length === 1 ? "row" : "rows"}
      </p>
      <p className="design-hint">
        One frame per row, from that row&apos;s script line and the board&apos;s art direction.
        Blank rows are skipped.
      </p>
      {error !== null && (
        <p className="design-error" role="alert">
          {error}
        </p>
      )}
      <button
        type="button"
        className="btn btn-primary design-generate"
        disabled={starting}
        onClick={() => void run()}
      >
        {starting ? "Starting…" : "Generate"}
      </button>
    </div>
  );
}

function describeRowError(error: unknown): string {
  if (!(error instanceof ApiError)) {
    return error instanceof Error ? error.message : "Something went wrong.";
  }
  switch (error.code) {
    case "design-not-configured":
      return "AI is not configured on this server yet (no OpenRouter key).";
    case "image-generation-unsupported":
      return "This server's AI model can't generate images. An admin can pick an image model in Settings.";
    case "no-art-direction":
      return "Write the design theme first — frames are generated from it.";
    case "no-rows-with-script":
      return "None of the selected rows have any script yet.";
    case "too-many-rows":
      return `That's more rows than one run allows (max ${MAX_ROW_DESIGNS}).`;
    case "target-is-source":
      return "Designs can't go in the Original Script column — pick another one.";
    case "already-running":
      return "A generation is already running on this board.";
    case "rate-limited":
    case "too-many-concurrent":
      return "You've generated a lot recently — give it a minute.";
    case "server-draining":
      return "The server is restarting. Try again in a few seconds.";
    default:
      return error.message;
  }
}

registerToolbarAction({
  id: "design-rows",
  appliesTo: "cells",
  tooltip: "Generate a design for each selected row",
  // After translate (20) — script first, pictures second, which is also the
  // order the two features are used in.
  order: 30,
  visible: (ctx) => isRowDesignVisible(eligibilityFor(ctx)),
  enabled: (ctx) => eligibilityFor(ctx).ok,
  icon: () => iconDesignRows,
  popover: (ctx) => <DesignRowsPopover ctx={ctx} />,
});
